import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchItems, fetchCategories } from '../../store/slices/inventorySlice';
import apiService from '../../services/api';
import { formatCurrency } from '../../utils/format';
import {
  TIME_PERIODS,
  getPeriodDateRange,
  getPreviousPeriodDateRange,
  getTodayDateRange,
  getPeriodComparisonLabels,
  fetchSalesTimeSeries,
  fetchProductSalesForRange,
  fetchSalesTotalsForRange,
  fetchAllOrdersForRange,
  fetchAllRefundsForRange,
  getTopSoldProducts,
} from '../../utils/insightsSales';
import { subscribeToOrdersUpdates, subscribeToPurchasesUpdates, subscribeToExpensesUpdates } from '../../services/socket';
import { getTokens } from '../../utils/tokens';
import { MonoMoney } from '../ui/AdminPagePrimitives';
import {
  ShoppingCart,
  DollarSign,
  Package,
  TrendingUp,
  TrendingDown,
  RotateCcw,
  AlertTriangle,
  Banknote,
  CreditCard,
  Smartphone,
  Wallet,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [timePeriod, setTimePeriod] = useState(TIME_PERIODS.LAST_7_DAYS);
  const [stats, setStats] = useState({
    todaySales: 0,
    todayRevenue: 0,
    todayTransactions: 0,
    lowStockItems: 0,
  });
  const [mostSoldProducts, setMostSoldProducts] = useState([]);
  const [mostRefundedProducts, setMostRefundedProducts] = useState([]);
  const [revenueComparison, setRevenueComparison] = useState({
    current: 0,
    previous: 0,
    change: 0,
    salesChange: 0,
    currentLabel: '',
    previousLabel: '',
    periodSales: 0,
    periodProfit: 0,
  });
  const [revenueBreakdown, setRevenueBreakdown] = useState([]);
  const [expensesChartData, setExpensesChartData] = useState([]);
  const [purchasesChartData, setPurchasesChartData] = useState([]);
  const [paymentMethodsData, setPaymentMethodsData] = useState([]);
  const [refundsChartData, setRefundsChartData] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [allRefunds, setAllRefunds] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);
  const [allPurchases, setAllPurchases] = useState([]);

  const { user } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const { items } = useSelector((state) => state.inventory);
  const [lowStockThreshold, setLowStockThreshold] = useState(10);
  const { t, isRTL, language } = useLanguage();
  const chartLocale = language === 'ar' ? 'ar-EG' : 'en-US';

  const tk = getTokens(mode);
  const isDark = mode === 'black' || mode === 'dark';

  const getMonthNames = () =>
    Array.from({ length: 12 }, (_, i) =>
      new Date(2000, i, 1).toLocaleDateString(chartLocale, { month: 'short' })
    );

  const accentMain = tk.accent;
  const COLORS = [
    tk.accent,
    tk.success,
    tk.warning,
    tk.danger,
    tk.cat ? tk.cat[4] : '#7c3aed',
  ];
  const REFUND_PIE_COLORS = tk.cat
    ? [...tk.cat, '#84cc16', '#f97316', '#0ea5e9', '#14b8a6', '#be123c']
    : [
        accentMain,
        isDark ? '#2dd4bf' : '#0d9488',
        isDark ? '#fbbf24' : '#d97706',
        isDark ? '#f87171' : '#dc2626',
        isDark ? '#a78bfa' : '#7c3aed',
        isDark ? '#f472b6' : '#db2777',
        isDark ? '#22d3ee' : '#0891b2',
        '#84cc16',
        '#f97316',
        '#0ea5e9',
        '#14b8a6',
        '#be123c',
      ];

  const chartGridColor = isDark ? '#2a2c36' : '#e7e7e3';
  const axisTickStyle = { fill: tk.textMuted, fontSize: 10 };
  const tooltipStyle = {
    contentStyle: {
      background: tk.surface,
      border: `1px solid ${tk.border}`,
      borderRadius: 8,
      color: tk.text,
      fontSize: 12,
    },
    labelStyle: { color: tk.textSecondary },
    itemStyle: { color: tk.text },
  };

  const formatRefundPieHoverLine = useCallback((entry) => {
    if (!entry) return '';
    const nm = String(entry.fullName ?? entry.name ?? '').trim();
    const qtyNum = Number(entry.quantity);

    const qtyStr = (() => {
      if (!Number.isFinite(qtyNum)) return String(entry.quantity ?? '0');
      if (entry.unitType === 'kg') return parseFloat(qtyNum.toFixed(3)).toString();
      return Number.isInteger(qtyNum) || Math.abs(qtyNum - Math.round(qtyNum)) < 1e-6
        ? String(Math.round(qtyNum))
        : parseFloat(qtyNum.toFixed(3)).toString();
    })();

    if (entry.isOthers) return `${nm}: ${qtyStr}`;
    const ut = entry.unitType;
    if (!ut) return `${nm}: ${qtyStr}`;
    const unitLbl = ut === 'kg' ? t('inventory.perKg') : t('inventory.perPiece');
    return `${nm}: ${qtyStr} ${unitLbl}`;
  }, [t]);

  const truncateRefundLegendName = useCallback((label) => {
    const s = String(label ?? '').replace(/\s+/g, ' ').trim();
    if (s.length <= 25) return s;
    return `${s.substring(0, 22)}...`;
  }, []);

  const renderRefundPieLegend = useCallback(({ payload }) => {
    if (!payload?.length) return null;
    const sorted = [...payload].sort(
      (a, b) => Number(b.payload?.quantity ?? 0) - Number(a.payload?.quantity ?? 0)
    );
    const totalQty = sorted.reduce(
      (sum, entry) => sum + Number(entry.payload?.quantity ?? 0),
      0
    );
    return (
      <ul
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          direction: isRTL ? 'rtl' : 'ltr',
          gap: '6px 14px',
          padding: '13px 0 0',
          margin: 0,
          listStyle: 'none',
        }}
      >
        {sorted.map((entry, index) => (
          <li
            key={`${entry.value}-${index}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 10,
              color: tk.textSecondary,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                backgroundColor: entry.color,
                flexShrink: 0,
              }}
            />
            <span>
              {entry.value}{' '}
              <span dir="ltr" style={{ opacity: 0.85 }}>
                ({totalQty > 0 ? Math.round((Number(entry.payload?.quantity ?? 0) / totalQty) * 100) : 0}%)
              </span>
            </span>
          </li>
        ))}
      </ul>
    );
  }, [isRTL, tk.textSecondary]);

  const formatAxisCompactValue = useCallback((value) => {
    const num = Number(value) || 0;
    const absNum = Math.abs(num);

    const formatWithUnit = (divisor, unit) => {
      const compact = num / divisor;
      const rounded = Math.round(compact * 10) / 10;
      const isWhole = Number.isInteger(rounded);
      return `${isWhole ? rounded.toFixed(0) : rounded.toFixed(1)}${unit}`;
    };

    if (absNum > 999900000) return formatWithUnit(1000000000, 'B');
    if (absNum > 999900) return formatWithUnit(1000000, 'M');
    if (absNum > 9999) return formatWithUnit(1000, 'K');
    return Number.isInteger(num) ? String(num) : num.toFixed(1);
  }, []);

  const formatProductAxisLabel = useCallback((value) => {
    const normalized = String(value || 'Unknown').replace(/\s+/g, ' ').trim();
    return normalized.length > 12 ? `${normalized.slice(0, 12)}...` : normalized;
  }, []);

  // Filter orders based on selected time period
  const filteredOrders = useMemo(() => {
    const { startDate, endDate } = getPeriodDateRange(timePeriod);
    return allOrders.filter((order) => {
      const orderDate = new Date(order.created_at);
      return orderDate >= startDate && orderDate <= new Date(endDate.getTime() + 86400000);
    });
  }, [allOrders, timePeriod]);

  // Filter refunds based on selected time period
  const filteredRefunds = useMemo(() => {
    const { startDate, endDate } = getPeriodDateRange(timePeriod);
    return allRefunds.filter((refund) => {
      const refundDate = new Date(refund.created_at);
      return refundDate >= startDate && refundDate <= new Date(endDate.getTime() + 86400000);
    });
  }, [allRefunds, timePeriod]);

  // Filter expenses based on selected time period
  const filteredExpenses = useMemo(() => {
    const { startDate, endDate } = getPeriodDateRange(timePeriod);
    return allExpenses.filter((exp) => {
      const d = new Date(exp.bought_at || exp.created_at);
      return d >= startDate && d <= new Date(endDate.getTime() + 86400000);
    });
  }, [allExpenses, timePeriod]);

  const filteredPurchases = useMemo(() => {
    const { startDate, endDate } = getPeriodDateRange(timePeriod);
    return allPurchases.filter((p) => {
      const d = new Date(p.purchased_at || p.created_at);
      return d >= startDate && d <= new Date(endDate.getTime() + 86400000);
    });
  }, [allPurchases, timePeriod]);

  const fetchAnalyticsData = useCallback(async (period) => {
    try {
      const range = getPeriodDateRange(period);

      const [orders, refundsRes, expensesRes, purchasesRes] = await Promise.all([
        fetchAllOrdersForRange(apiService, range),
        fetchAllRefundsForRange(apiService, range),
        apiService.getExpenses({ start_date: range.start_date, end_date: range.end_date }),
        apiService.getPurchases({ start_date: range.start_date, end_date: range.end_date }),
      ]);

      const toArr = (r) => (Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []);
      setAllOrders(orders);
      setAllRefunds(refundsRes);
      setAllExpenses(toArr(expensesRes));
      setAllPurchases(toArr(purchasesRes));
    } catch (err) {
      console.error('Failed to fetch analytics data:', err);
    }
  }, []);

  const fetchSalesMetrics = useCallback(async () => {
    try {
      const labels = getPeriodComparisonLabels(timePeriod, chartLocale, t);
      const todayRange = getTodayDateRange();
      const [timeSeries, productSales, prevTotals, todayTotals, todayOrders] = await Promise.all([
        fetchSalesTimeSeries(apiService, timePeriod, chartLocale, t),
        fetchProductSalesForRange(apiService, getPeriodDateRange(timePeriod)),
        fetchSalesTotalsForRange(apiService, getPreviousPeriodDateRange(timePeriod)),
        fetchSalesTotalsForRange(apiService, todayRange),
        fetchAllOrdersForRange(apiService, todayRange),
      ]);

      setRevenueBreakdown(timeSeries.breakdown);
      setMostSoldProducts(getTopSoldProducts(productSales.rows, 10));

      const currentSales = timeSeries.totals.sales;
      const currentProfit = timeSeries.totals.profit;
      const previousSales = parseFloat(prevTotals?.revenue || 0);
      const previousProfit = parseFloat(prevTotals?.profit || 0);

      const change =
        previousProfit > 0 ? ((currentProfit - previousProfit) / previousProfit) * 100 : 0;
      const salesChange =
        previousSales > 0 ? ((currentSales - previousSales) / previousSales) * 100 : 0;

      setRevenueComparison({
        current: currentProfit,
        previous: previousProfit,
        change,
        salesChange,
        currentLabel: labels.currentLabel,
        previousLabel: labels.previousLabel,
        periodSales: currentSales,
        periodProfit: currentProfit,
      });

      const todayOrdersCount = todayOrders.length;
      setStats((prev) => ({
        ...prev,
        todaySales: parseFloat(todayTotals?.revenue || 0),
        todayRevenue: parseFloat(todayTotals?.profit || 0),
        todayTransactions: todayOrdersCount,
      }));
    } catch (err) {
      console.error('Failed to fetch sales metrics:', err);
    }
  }, [timePeriod, chartLocale, t]);

  useEffect(() => {
    dispatch(fetchItems());
    dispatch(fetchCategories());

    const fetchStoreSettings = async () => {
      try {
        const res = await apiService.getStore();
        if (res.data?.low_stock_threshold) {
          setLowStockThreshold(res.data.low_stock_threshold);
        }
      } catch (err) {
        console.error('Failed to fetch store settings:', err);
      }
    };
    fetchStoreSettings();
    fetchAnalyticsData(timePeriod);
  }, [dispatch, fetchAnalyticsData, timePeriod]);

  useEffect(() => {
    fetchSalesMetrics();
  }, [fetchSalesMetrics]);

  useEffect(() => {
    const refresh = () => {
      fetchAnalyticsData(timePeriod);
      fetchSalesMetrics();
    };
    const unsubOrders = subscribeToOrdersUpdates(refresh);
    const unsubPurchases = subscribeToPurchasesUpdates(refresh);
    const unsubExpenses = subscribeToExpensesUpdates(refresh);
    return () => {
      unsubOrders();
      unsubPurchases();
      unsubExpenses();
    };
  }, [fetchAnalyticsData, fetchSalesMetrics, timePeriod]);

  useEffect(() => {
    const lowStockCount = (items || []).filter(
      (item) => (item.stock_quantity || 0) < lowStockThreshold
    ).length;
    setStats((prev) => ({ ...prev, lowStockItems: lowStockCount }));
  }, [items, lowStockThreshold]);

  // Calculate most refunded products based on filtered refunds
  useEffect(() => {
    if (filteredRefunds.length === 0) {
      setMostRefundedProducts([]);
      return;
    }

    const productRefunds = {};
    filteredRefunds.forEach((refund) => {
      if (refund.items && Array.isArray(refund.items)) {
        refund.items.forEach((item) => {
          const key = item.product_name || item.product_id;
          const fullName = String(item.product_name || 'Unknown').replace(/\s+/g, ' ').trim();
          const rawUt = item.unit_type;
          const incomingUt =
            rawUt != null && String(rawUt).trim() !== ''
              ? String(rawUt).trim().toLowerCase() === 'kg'
                ? 'kg'
                : 'piece'
              : null;

          if (!productRefunds[key]) {
            productRefunds[key] = {
              fullName,
              name: truncateRefundLegendName(fullName),
              quantity: 0,
              amount: 0,
              unitType: incomingUt,
            };
          } else {
            const existingUt = productRefunds[key].unitType;
            if (incomingUt) {
              if (!existingUt) productRefunds[key].unitType = incomingUt;
              else if (existingUt !== incomingUt) productRefunds[key].unitType = null;
            }
          }
          productRefunds[key].quantity += parseFloat(item.refunded_quantity || 0);
          productRefunds[key].amount += parseFloat(item.refunded_amount || 0);
        });
      }
    });

    const sorted = Object.values(productRefunds).sort((a, b) => b.quantity - a.quantity);
    const totalQty = sorted.reduce((s, p) => s + Number(p.quantity || 0), 0);

    if (totalQty <= 0) {
      setMostRefundedProducts([]);
      return;
    }

    const MIN_SHARE = 0.01; // 1% — smaller slices roll into Others
    const main = [];
    const small = [];
    for (const p of sorted) {
      const qty = Number(p.quantity || 0);
      const share = qty / totalQty;
      if (share >= MIN_SHARE) main.push({ ...p });
      else small.push(p);
    }

    let result = main.map((p) => ({ ...p }));

    if (small.length > 0) {
      const othersLabel = t('common.others');
      const agg = small.reduce(
        (acc, p) => ({
          quantity: acc.quantity + p.quantity,
          amount: acc.amount + p.amount,
        }),
        { quantity: 0, amount: 0 }
      );
      result.push({
        fullName: othersLabel,
        name: truncateRefundLegendName(othersLabel),
        quantity: agg.quantity,
        amount: agg.amount,
        isOthers: true,
        unitType: null,
      });
    }

    result.sort((a, b) => Number(b.quantity || 0) - Number(a.quantity || 0));
    setMostRefundedProducts(result);
  }, [filteredRefunds, t, truncateRefundLegendName]);

  // Build expenses, purchases, refunds chart data and payment methods
  useEffect(() => {
    const now = new Date();
    const monthNames = getMonthNames();

    const bucketExpenses = () => {
      let breakdown = [];
      if (
        timePeriod === TIME_PERIODS.THIS_YEAR ||
        timePeriod === TIME_PERIODS.LAST_YEAR
      ) {
        const year =
          timePeriod === TIME_PERIODS.THIS_YEAR
            ? now.getFullYear()
            : now.getFullYear() - 1;
        for (let i = 0; i < 12; i++)
          breakdown.push({ name: monthNames[i], amount: 0 });
        filteredExpenses.forEach((e) => {
          const d = new Date(e.bought_at);
          if (d.getFullYear() === year)
            breakdown[d.getMonth()].amount += parseFloat(e.amount || 0);
        });
      } else if (
        timePeriod === TIME_PERIODS.THIS_MONTH ||
        timePeriod === TIME_PERIODS.LAST_MONTH
      ) {
        const m =
          timePeriod === TIME_PERIODS.THIS_MONTH
            ? now.getMonth()
            : now.getMonth() === 0
            ? 11
            : now.getMonth() - 1;
        const y =
          timePeriod === TIME_PERIODS.THIS_MONTH
            ? now.getFullYear()
            : now.getMonth() === 0
            ? now.getFullYear() - 1
            : now.getFullYear();
        const weeks = Math.ceil(new Date(y, m + 1, 0).getDate() / 7);
        for (let i = 0; i < weeks; i++)
          breakdown.push({ name: `${t('common.week')} ${i + 1}`, amount: 0 });
        filteredExpenses.forEach((e) => {
          const d = new Date(e.bought_at);
          if (d.getMonth() === m && d.getFullYear() === y) {
            const wi = Math.min(Math.floor((d.getDate() - 1) / 7), weeks - 1);
            breakdown[wi].amount += parseFloat(e.amount || 0);
          }
        });
      } else {
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          breakdown.push({
            name: date.toLocaleDateString(chartLocale, { weekday: 'short' }),
            amount: 0,
            dateKey: date.toISOString().split('T')[0],
          });
        }
        filteredExpenses.forEach((e) => {
          const dateKey = new Date(e.bought_at).toISOString().split('T')[0];
          const day = breakdown.find((d) => d.dateKey === dateKey);
          if (day) day.amount += parseFloat(e.amount || 0);
        });
      }
      return breakdown;
    };

    const bucketPurchases = () => {
      let breakdown = [];
      if (
        timePeriod === TIME_PERIODS.THIS_YEAR ||
        timePeriod === TIME_PERIODS.LAST_YEAR
      ) {
        const year =
          timePeriod === TIME_PERIODS.THIS_YEAR
            ? now.getFullYear()
            : now.getFullYear() - 1;
        for (let i = 0; i < 12; i++)
          breakdown.push({ name: monthNames[i], amount: 0 });
        filteredPurchases.forEach((p) => {
          const d = new Date(p.purchased_at);
          if (d.getFullYear() === year)
            breakdown[d.getMonth()].amount += parseFloat(p.total_price || 0);
        });
      } else if (
        timePeriod === TIME_PERIODS.THIS_MONTH ||
        timePeriod === TIME_PERIODS.LAST_MONTH
      ) {
        const m =
          timePeriod === TIME_PERIODS.THIS_MONTH
            ? now.getMonth()
            : now.getMonth() === 0
            ? 11
            : now.getMonth() - 1;
        const y =
          timePeriod === TIME_PERIODS.THIS_MONTH
            ? now.getFullYear()
            : now.getMonth() === 0
            ? now.getFullYear() - 1
            : now.getFullYear();
        const weeks = Math.ceil(new Date(y, m + 1, 0).getDate() / 7);
        for (let i = 0; i < weeks; i++)
          breakdown.push({ name: `${t('common.week')} ${i + 1}`, amount: 0 });
        filteredPurchases.forEach((p) => {
          const d = new Date(p.purchased_at);
          if (d.getMonth() === m && d.getFullYear() === y) {
            const wi = Math.min(Math.floor((d.getDate() - 1) / 7), weeks - 1);
            breakdown[wi].amount += parseFloat(p.total_price || 0);
          }
        });
      } else {
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          breakdown.push({
            name: date.toLocaleDateString(chartLocale, { weekday: 'short' }),
            amount: 0,
            dateKey: date.toISOString().split('T')[0],
          });
        }
        filteredPurchases.forEach((p) => {
          const dateKey = new Date(p.purchased_at).toISOString().split('T')[0];
          const day = breakdown.find((d) => d.dateKey === dateKey);
          if (day) day.amount += parseFloat(p.total_price || 0);
        });
      }
      return breakdown;
    };

    const bucketRefunds = () => {
      let breakdown = [];
      if (
        timePeriod === TIME_PERIODS.THIS_YEAR ||
        timePeriod === TIME_PERIODS.LAST_YEAR
      ) {
        const year =
          timePeriod === TIME_PERIODS.THIS_YEAR
            ? now.getFullYear()
            : now.getFullYear() - 1;
        for (let i = 0; i < 12; i++)
          breakdown.push({ name: monthNames[i], amount: 0 });
        filteredRefunds.forEach((r) => {
          const d = new Date(r.created_at);
          if (d.getFullYear() === year)
            breakdown[d.getMonth()].amount += parseFloat(r.amount || 0);
        });
      } else if (
        timePeriod === TIME_PERIODS.THIS_MONTH ||
        timePeriod === TIME_PERIODS.LAST_MONTH
      ) {
        const m =
          timePeriod === TIME_PERIODS.THIS_MONTH
            ? now.getMonth()
            : now.getMonth() === 0
            ? 11
            : now.getMonth() - 1;
        const y =
          timePeriod === TIME_PERIODS.THIS_MONTH
            ? now.getFullYear()
            : now.getMonth() === 0
            ? now.getFullYear() - 1
            : now.getFullYear();
        const weeks = Math.ceil(new Date(y, m + 1, 0).getDate() / 7);
        for (let i = 0; i < weeks; i++)
          breakdown.push({ name: `${t('common.week')} ${i + 1}`, amount: 0 });
        filteredRefunds.forEach((r) => {
          const d = new Date(r.created_at);
          if (d.getMonth() === m && d.getFullYear() === y) {
            const wi = Math.min(Math.floor((d.getDate() - 1) / 7), weeks - 1);
            breakdown[wi].amount += parseFloat(r.amount || 0);
          }
        });
      } else {
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          breakdown.push({
            name: date.toLocaleDateString(chartLocale, { weekday: 'short' }),
            amount: 0,
            dateKey: date.toISOString().split('T')[0],
          });
        }
        filteredRefunds.forEach((r) => {
          const dateKey = new Date(r.created_at).toISOString().split('T')[0];
          const day = breakdown.find((d) => d.dateKey === dateKey);
          if (day) day.amount += parseFloat(r.amount || 0);
        });
      }
      return breakdown;
    };

    const bucketPaymentMethods = () => {
      let breakdown = [];
      const initRow = () => ({ cash: 0, card: 0, instapay: 0, wallet: 0 });

      const addToBucket = (row, method, amount) => {
        const amt = parseFloat(amount || 0);
        const m = (method || '').toLowerCase();
        if (row[m] !== undefined) row[m] += amt;
      };

      if (
        timePeriod === TIME_PERIODS.THIS_YEAR ||
        timePeriod === TIME_PERIODS.LAST_YEAR
      ) {
        const year =
          timePeriod === TIME_PERIODS.THIS_YEAR
            ? now.getFullYear()
            : now.getFullYear() - 1;
        for (let i = 0; i < 12; i++)
          breakdown.push({ name: monthNames[i], ...initRow() });
        filteredOrders.forEach((o) => {
          const d = new Date(o.created_at);
          if (d.getFullYear() !== year) return;
          const row = breakdown[d.getMonth()];
          const payments = o.payments || [];
          if (payments.length > 0) {
            payments.forEach((p) =>
              addToBucket(row, (p.method || '').toLowerCase(), p.amount)
            );
          } else if (o.payment_method && o.payment_method !== 'mixed') {
            addToBucket(row, o.payment_method, o.total_amount);
          }
        });
      } else if (
        timePeriod === TIME_PERIODS.THIS_MONTH ||
        timePeriod === TIME_PERIODS.LAST_MONTH
      ) {
        const m =
          timePeriod === TIME_PERIODS.THIS_MONTH
            ? now.getMonth()
            : now.getMonth() === 0
            ? 11
            : now.getMonth() - 1;
        const y =
          timePeriod === TIME_PERIODS.THIS_MONTH
            ? now.getFullYear()
            : now.getMonth() === 0
            ? now.getFullYear() - 1
            : now.getFullYear();
        const weeks = Math.ceil(new Date(y, m + 1, 0).getDate() / 7);
        for (let i = 0; i < weeks; i++)
          breakdown.push({ name: `${t('common.week')} ${i + 1}`, ...initRow() });
        filteredOrders.forEach((o) => {
          const d = new Date(o.created_at);
          if (d.getMonth() !== m || d.getFullYear() !== y) return;
          const wi = Math.min(Math.floor((d.getDate() - 1) / 7), weeks - 1);
          const row = breakdown[wi];
          const payments = o.payments || [];
          if (payments.length > 0) {
            payments.forEach((p) =>
              addToBucket(row, (p.method || '').toLowerCase(), p.amount)
            );
          } else if (o.payment_method && o.payment_method !== 'mixed') {
            addToBucket(row, o.payment_method, o.total_amount);
          }
        });
      } else {
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          breakdown.push({
            name: date.toLocaleDateString(chartLocale, { weekday: 'short' }),
            dateKey: date.toISOString().split('T')[0],
            ...initRow(),
          });
        }
        filteredOrders.forEach((o) => {
          const dateKey = new Date(o.created_at).toISOString().split('T')[0];
          const row = breakdown.find((d) => d.dateKey === dateKey);
          if (!row) return;
          const payments = o.payments || [];
          if (payments.length > 0) {
            payments.forEach((p) =>
              addToBucket(row, (p.method || '').toLowerCase(), p.amount)
            );
          } else if (o.payment_method && o.payment_method !== 'mixed') {
            addToBucket(row, o.payment_method, o.total_amount);
          }
        });
      }
      return breakdown;
    };

    setExpensesChartData(bucketExpenses());
    setPurchasesChartData(bucketPurchases());
    setRefundsChartData(bucketRefunds());
    setPaymentMethodsData(bucketPaymentMethods());
  }, [filteredExpenses, filteredPurchases, filteredRefunds, filteredOrders, timePeriod, chartLocale, t]);

  const getTimePeriodLabel = () => {
    switch (timePeriod) {
      case TIME_PERIODS.LAST_7_DAYS: return t('common.last7Days');
      case TIME_PERIODS.LAST_MONTH: return t('common.lastMonth');
      case TIME_PERIODS.THIS_MONTH: return t('common.thisMonth');
      case TIME_PERIODS.LAST_YEAR: return t('common.lastYear');
      case TIME_PERIODS.THIS_YEAR: return t('common.thisYear');
      default: return t('common.last7Days');
    }
  };

  // Payment split percentages for the right panel
  const paymentSplitTotals = useMemo(() => {
    const totals = { cash: 0, card: 0, instapay: 0, wallet: 0 };
    paymentMethodsData.forEach((row) => {
      totals.cash += row.cash || 0;
      totals.card += row.card || 0;
      totals.instapay += row.instapay || 0;
      totals.wallet += row.wallet || 0;
    });
    const grand = totals.cash + totals.card + totals.instapay + totals.wallet || 1;
    return {
      cash: (totals.cash / grand) * 100,
      card: (totals.card / grand) * 100,
      instapay: (totals.instapay / grand) * 100,
      wallet: (totals.wallet / grand) * 100,
    };
  }, [paymentMethodsData]);

  // Card styles
  const cardStyle = {
    background: tk.surface,
    border: `1px solid ${tk.border}`,
    borderRadius: 12,
    padding: 16,
  };

  const chartCardStyle = {
    ...cardStyle,
    display: 'flex',
    flexDirection: 'column',
  };

  const cardTitleStyle = {
    fontSize: 12.5,
    fontWeight: 600,
    color: tk.text,
    marginBottom: 12,
    flexShrink: 0,
  };

  const timePeriodButtons = [
    { value: TIME_PERIODS.LAST_7_DAYS, label: t('common.last7Days') },
    { value: TIME_PERIODS.THIS_MONTH, label: t('common.thisMonth') },
    { value: TIME_PERIODS.LAST_MONTH, label: t('common.lastMonth') },
    { value: TIME_PERIODS.THIS_YEAR, label: t('common.thisYear') },
    { value: TIME_PERIODS.LAST_YEAR, label: t('common.lastYear') },
  ];

  const statCards = [
    {
      icon: DollarSign,
      label: t('dashboard.todaySales'),
      value: stats.todaySales,
      isMoney: true,
      iconColor: tk.success,
      iconBg: tk.successSoft,
    },
    {
      icon: ShoppingCart,
      label: t('dashboard.totalOrders'),
      value: stats.todayTransactions,
      isMoney: false,
      iconColor: tk.accent,
      iconBg: tk.accentSoft,
      onClick: () => navigate('/reports'),
    },
    {
      icon: TrendingUp,
      label: t('dashboard.todayProfit'),
      value: stats.todayRevenue,
      isMoney: true,
      iconColor: tk.cat ? tk.cat[1] : tk.success,
      iconBg: tk.successSoft,
    },
    {
      icon: AlertTriangle,
      label: t('dashboard.lowStockItems'),
      value: stats.lowStockItems,
      isMoney: false,
      iconColor: tk.warning,
      iconBg: tk.warningSoft,
      onClick: () => navigate('/inventory'),
    },
  ];

  return (
    <div
      style={{
        background: tk.bg,
        color: tk.text,
        padding: 20,
        paddingBottom: 16,
        minHeight: '100%',
        fontFamily: language === 'ar' ? '"Cairo",sans-serif' : '"DM Sans",sans-serif',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`theme-${mode}`}
    >
      {/* [1] Time period filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flexShrink: 0 }}>
        {timePeriodButtons.map(({ value, label }) => {
          const isActive = timePeriod === value;
          return (
            <button
              key={value}
              onClick={() => setTimePeriod(value)}
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 8,
                border: isActive ? '1px solid transparent' : `1px solid ${tk.border}`,
                background: isActive ? tk.accentSoft : 'transparent',
                color: isActive ? tk.accentText : tk.textSecondary,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </button>
          );
        })}
        <span
          style={{
            marginInlineStart: 'auto',
            fontSize: 13,
            fontWeight: 700,
            color: tk.textMuted,
            fontFamily: '"JetBrains Mono", monospace',
          }}
        >
          {new Date().toLocaleDateString(chartLocale, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      </div>

      {/* [2] Stat strip — 4-column grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
          flexShrink: 0,
        }}
      >
        {statCards.map(({ icon: Icon, label, value, isMoney, iconColor, iconBg, onClick }, idx) => (
          <div
            key={idx}
            style={{
              ...cardStyle,
              cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={onClick}
          >
            {/* Icon box */}
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: 6,
                background: iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 10,
              }}
            >
              <Icon size={13} style={{ color: iconColor }} />
            </div>
            {/* Label */}
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                color: tk.textMuted,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: 4,
              }}
            >
              {label}
            </div>
            {/* Value */}
            {isMoney ? (
              <MonoMoney amount={value} tk={tk} size={20} weight={700} />
            ) : (
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  fontFamily: '"JetBrains Mono", monospace',
                  color: tk.text,
                  lineHeight: 1,
                }}
              >
                {value}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* [3] Revenue chart row — 2fr 1fr */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        {/* LEFT: Revenue chart */}
        <div style={chartCardStyle}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
              flexShrink: 0,
            }}
          >
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: tk.text }}>
                {t('dashboard.revenueComparison')}
              </div>
              <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 2 }} dir="ltr">
                {revenueComparison.previousLabel
                  ? isRTL
                    ? `${revenueComparison.previousLabel} vs ${revenueComparison.currentLabel}`
                    : `${revenueComparison.currentLabel} vs ${revenueComparison.previousLabel}`
                  : revenueComparison.currentLabel}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  marginTop: 8,
                  fontSize: 11,
                }}
              >
                <span style={{ color: tk.textSecondary }}>
                  {t('sales.totalRevenue')}:{' '}
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, color: isDark ? 'rgba(6,182,212,0.95)' : 'rgba(8,145,178,0.95)' }}>
                    {formatCurrency(revenueComparison.periodSales || 0)}
                  </span>
                </span>
                <span style={{ color: tk.textSecondary }}>
                  {t('sales.revenueProfit')}:{' '}
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, color: isDark ? 'rgba(99,102,241,0.95)' : 'rgba(79,70,229,0.95)' }}>
                    {formatCurrency(revenueComparison.periodProfit || 0)}
                  </span>
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* Revenue trend badge */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: '"JetBrains Mono", monospace',
                  color: revenueComparison.change >= 0 ? tk.successText : tk.dangerText,
                  borderBottom: `2px solid ${isDark ? 'rgba(99,102,241,0.8)' : 'rgba(79,70,229,0.7)'}`,
                  paddingBottom: 2,
                }}
              >
                {revenueComparison.change >= 0 ? (
                  <TrendingUp size={13} />
                ) : (
                  <TrendingDown size={13} />
                )}
                {revenueComparison.change >= 0 ? '+' : ''}
                {revenueComparison.change.toFixed(1)}%
              </div>
              {/* Sales trend badge */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: '"JetBrains Mono", monospace',
                  color: revenueComparison.salesChange >= 0 ? tk.successText : tk.dangerText,
                  borderBottom: `2px solid ${isDark ? 'rgba(6,182,212,0.9)' : 'rgba(8,145,178,0.8)'}`,
                  paddingBottom: 2,
                }}
              >
                {revenueComparison.salesChange >= 0 ? (
                  <TrendingUp size={13} />
                ) : (
                  <TrendingDown size={13} />
                )}
                {revenueComparison.salesChange >= 0 ? '+' : ''}
                {revenueComparison.salesChange.toFixed(1)}%
              </div>
            </div>
          </div>
          <div style={{ height: 210 }} dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={revenueBreakdown}
                margin={{ top: 4, right: 4, left: 0, bottom: 4 }}
                barCategoryGap="18%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartGridColor}
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={axisTickStyle}
                  axisLine={false}
                  tickLine={false}
                  reversed={isRTL}
                />
                <YAxis
                  tick={axisTickStyle}
                  axisLine={false}
                  tickLine={false}
                  orientation={isRTL ? 'right' : 'left'}
                  width={40}
                  tickFormatter={formatAxisCompactValue}
                />
                <Tooltip
                  contentStyle={tooltipStyle.contentStyle}
                  labelStyle={tooltipStyle.labelStyle}
                  itemStyle={tooltipStyle.itemStyle}
                  formatter={(value, name) => [formatCurrency(value), name]}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                  formatter={(value) => value}
                />
                <Bar
                  dataKey="revenue"
                  name={t('dashboard.chartProfitBarLabel')}
                  fill={isDark ? 'rgba(99, 102, 241, 0.72)' : 'rgba(79, 70, 229, 0.62)'}
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="sales"
                  name={t('dashboard.chartSales')}
                  fill={isDark ? 'rgba(6, 182, 212, 0.82)' : 'rgba(8, 145, 178, 0.72)'}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* RIGHT: Payment methods split */}
        <div style={chartCardStyle}>
          <div style={cardTitleStyle}>{t('dashboard.paymentSplit')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {[
              {
                id: 'cash',
                label: t('pos.cash'),
                pct: paymentSplitTotals.cash,
                color: tk.accent,
                Icon: Banknote,
              },
              {
                id: 'card',
                label: t('pos.card'),
                pct: paymentSplitTotals.card,
                color: tk.success,
                Icon: CreditCard,
              },
              {
                id: 'instapay',
                label: t('pos.instapay'),
                pct: paymentSplitTotals.instapay,
                color: tk.warning,
                Icon: Smartphone,
              },
              {
                id: 'wallet',
                label: t('pos.wallet'),
                pct: paymentSplitTotals.wallet,
                color: tk.cat ? tk.cat[3] : '#db2777',
                Icon: Wallet,
              },
            ].map(({ id, label, pct, color, Icon }) => (
              <div key={id} style={{ paddingBottom: 2 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 7,
                      fontSize: 11.5,
                      color: tk.textSecondary,
                      flexDirection: isRTL ? 'row-reverse' : 'row',
                    }}
                  >
                    <Icon size={14} color={color} strokeWidth={2} style={{ flexShrink: 0 }} />
                    {label}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontFamily: '"JetBrains Mono", monospace',
                      color: tk.text,
                      fontWeight: 600,
                    }}
                  >
                    {pct.toFixed(1)}%
                  </span>
                </div>
                <div
                  style={{
                    height: 6,
                    borderRadius: 99,
                    background: tk.sunken,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(100, pct)}%`,
                      background: color,
                      borderRadius: 99,
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* [4] Charts row — top selling + most refunded */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {/* LEFT: Top selling products */}
        <div style={{ ...chartCardStyle, minHeight: 260 }}>
          <div style={cardTitleStyle}>{t('dashboard.topSellingProducts')}</div>
          {mostSoldProducts.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tk.textMuted,
                fontSize: 12,
              }}
            >
              {t('dashboard.noSalesData')}
            </div>
          ) : (
            <div
              style={{ flex: 1, height: Math.max(220, mostSoldProducts.length * 24) }}
              dir="ltr"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={mostSoldProducts}
                  layout="vertical"
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={chartGridColor}
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={axisTickStyle}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatAxisCompactValue}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fill: tk.textMuted, fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    width={70}
                    interval={0}
                    tickFormatter={formatProductAxisLabel}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle.contentStyle}
                    labelStyle={tooltipStyle.labelStyle}
                  itemStyle={tooltipStyle.itemStyle}
                    formatter={(val) => {
                      const num = Number(val);
                      if (!Number.isFinite(num)) return [val, 'Qty Sold'];
                      const rounded = Math.round(num * 1000) / 1000;
                      return [String(rounded), 'Qty Sold'];
                    }}
                  />
                  <Bar
                    dataKey="quantity"
                    name="Qty Sold"
                    fill={accentMain}
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* RIGHT: Most refunded products donut */}
        <div style={{ ...chartCardStyle, minHeight: 260 }}>
          <div style={cardTitleStyle}>{t('dashboard.mostRefundedProducts')}</div>
          {mostRefundedProducts.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tk.textMuted,
                fontSize: 12,
              }}
            >
              {t('dashboard.noRefundData')}
            </div>
          ) : (
            <div style={{ height: 220 }} dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={mostRefundedProducts}
                    cx="50%"
                    cy="57%"
                    innerRadius="30%"
                    outerRadius="60%"
                    paddingAngle={2}
                    dataKey="quantity"
                    nameKey="name"
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {mostRefundedProducts.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={REFUND_PIE_COLORS[index % REFUND_PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    cursor={false}
                    contentStyle={tooltipStyle.contentStyle}
                    labelStyle={tooltipStyle.labelStyle}
                    itemStyle={tooltipStyle.itemStyle}
                    formatter={(value, name, props) => [formatRefundPieHoverLine(props.payload), '']}
                    separator=""
                  />
                  <Legend content={renderRefundPieLegend} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* [5] Bottom charts row — Expenses + Purchases */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        {/* Expenses */}
        <div style={{ ...chartCardStyle, minHeight: 220 }}>
          <div style={cardTitleStyle}>{t('dashboard.expensesChart')}</div>
          <div style={{ height: 180 }} dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={expensesChartData}
                margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartGridColor}
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={axisTickStyle}
                  axisLine={false}
                  tickLine={false}
                  reversed={isRTL}
                />
                <YAxis
                  tick={axisTickStyle}
                  axisLine={false}
                  tickLine={false}
                  orientation={isRTL ? 'right' : 'left'}
                  width={40}
                  tickFormatter={formatAxisCompactValue}
                />
                <Tooltip
                  contentStyle={tooltipStyle.contentStyle}
                  labelStyle={tooltipStyle.labelStyle}
                  itemStyle={tooltipStyle.itemStyle}
                  formatter={(val) => [formatCurrency(val), t('common.amount')]}
                />
                <Bar
                  dataKey="amount"
                  name={`${t('common.amount')} (EGP)`}
                  fill={tk.warning}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Purchases */}
        <div style={{ ...chartCardStyle, minHeight: 220 }}>
          <div style={cardTitleStyle}>{t('dashboard.purchasesChart')}</div>
          <div style={{ height: 180 }} dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={purchasesChartData}
                margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartGridColor}
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={axisTickStyle}
                  axisLine={false}
                  tickLine={false}
                  reversed={isRTL}
                />
                <YAxis
                  tick={axisTickStyle}
                  axisLine={false}
                  tickLine={false}
                  orientation={isRTL ? 'right' : 'left'}
                  width={40}
                  tickFormatter={formatAxisCompactValue}
                />
                <Tooltip
                  contentStyle={tooltipStyle.contentStyle}
                  labelStyle={tooltipStyle.labelStyle}
                  itemStyle={tooltipStyle.itemStyle}
                  formatter={(val) => [formatCurrency(val), t('common.amount')]}
                />
                <Bar
                  dataKey="amount"
                  name={`${t('common.amount')} (EGP)`}
                  fill={tk.cat ? tk.cat[4] : '#7c3aed'}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* [6] Payment methods detailed chart + Refunds chart */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        {/* Payment Methods Grouped Bar */}
        <div style={{ ...chartCardStyle, minHeight: 220 }}>
          <div style={cardTitleStyle}>{t('dashboard.paymentMethods')}</div>
          <div style={{ height: 180 }} dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={paymentMethodsData}
                margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartGridColor}
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={axisTickStyle}
                  axisLine={false}
                  tickLine={false}
                  reversed={isRTL}
                />
                <YAxis
                  tick={axisTickStyle}
                  axisLine={false}
                  tickLine={false}
                  orientation={isRTL ? 'right' : 'left'}
                  width={40}
                  tickFormatter={formatAxisCompactValue}
                />
                <Tooltip
                  contentStyle={tooltipStyle.contentStyle}
                  labelStyle={tooltipStyle.labelStyle}
                  formatter={(val, name) => [formatCurrency(val), name]}
                />
                <Legend
                  wrapperStyle={{ fontSize: 10, color: tk.textSecondary }}
                />
                <Bar dataKey="cash" name={t('pos.cash')} fill={tk.accent} radius={[4, 4, 0, 0]} />
                <Bar dataKey="card" name={t('pos.card')} fill={tk.success} radius={[4, 4, 0, 0]} />
                <Bar dataKey="instapay" name={t('pos.instapay')} fill={tk.warning} radius={[4, 4, 0, 0]} />
                <Bar
                  dataKey="wallet"
                  name={t('pos.wallet')}
                  fill={tk.cat ? tk.cat[3] : '#db2777'}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Refunds chart */}
        <div style={{ ...chartCardStyle, minHeight: 220 }}>
          <div style={cardTitleStyle}>{t('dashboard.refundsChart')}</div>
          <div style={{ height: 180 }} dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={refundsChartData}
                margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chartGridColor}
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={axisTickStyle}
                  axisLine={false}
                  tickLine={false}
                  reversed={isRTL}
                />
                <YAxis
                  tick={axisTickStyle}
                  axisLine={false}
                  tickLine={false}
                  orientation={isRTL ? 'right' : 'left'}
                  width={40}
                  tickFormatter={formatAxisCompactValue}
                />
                <Tooltip
                  contentStyle={tooltipStyle.contentStyle}
                  labelStyle={tooltipStyle.labelStyle}
                  itemStyle={tooltipStyle.itemStyle}
                  formatter={(val) => [formatCurrency(val), t('dashboard.refunds')]}
                />
                <Bar
                  dataKey="amount"
                  name={`${t('dashboard.refunds')} (EGP)`}
                  fill={tk.danger}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
